import { FormControl, FormErrorMessage, FormHelperText, FormLabel, Select, Stack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FoaPart, Regulation } from "../../autogen/bff-api";
import type { FieldError } from "../../common/editing/save-coordinator/types";
import { useFieldErrorTranslation } from "../../common/editing/save-coordinator/useFieldErrorTranslation";
import { SUPPORTED_REGULATIONS } from "../../common/procurement/publicProcurementOptions";

const FOA_PARTS: readonly FoaPart[] = [FoaPart.OutsideFoa, FoaPart.PartI, FoaPart.PartIi, FoaPart.PartIii, FoaPart.PartIv];

export interface FrameworkRegulation {
  regulation: Regulation | null;
  foaPart: FoaPart | null;
}

export const isComplete = ({ regulation, foaPart }: FrameworkRegulation): boolean =>
  regulation !== null && (regulation !== Regulation.Foa || foaPart !== null);

const lacksFoaPart = ({ regulation, foaPart }: FrameworkRegulation): boolean =>
  regulation === Regulation.Foa && foaPart === null;

interface Props {
  idPrefix: string;
  value: FrameworkRegulation;
  onChange: (value: FrameworkRegulation) => void;
  isRequired?: boolean;
  helperText?: string;
  errors?: { regulation?: FieldError; foaPart?: FieldError };
}

export const FrameworkRegulationFields = ({ idPrefix, value, onChange, isRequired = false, helperText, errors }: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  return (
    <Stack spacing="3">
      <FormControl isRequired={isRequired} isInvalid={!!errors?.regulation}>
        <FormLabel htmlFor={`${idPrefix}-regulation`}>{t("Regulation")}</FormLabel>
        <Select
          id={`${idPrefix}-regulation`}
          backgroundColor="smBackground"
          placeholder={t("Choose a regulation") ?? undefined}
          value={value.regulation ?? ""}
          onChange={(e) => {
            const regulation = (e.target.value || null) as Regulation | null;
            onChange({ regulation, foaPart: regulation === Regulation.Foa ? value.foaPart : null });
          }}
        >
          {SUPPORTED_REGULATIONS.map((regulation) => (
            <option key={regulation} value={regulation}>
              {t(regulation)}
            </option>
          ))}
        </Select>
        {errors?.regulation && <FormErrorMessage>{formatFieldError(errors.regulation)}</FormErrorMessage>}
        {helperText && value.regulation !== Regulation.Foa && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
      {value.regulation === Regulation.Foa && (
        <FormControl isRequired isInvalid={lacksFoaPart(value) || !!errors?.foaPart}>
          <FormLabel htmlFor={`${idPrefix}-foa-part`}>{t("Part of FOA")}</FormLabel>
          <Select
            id={`${idPrefix}-foa-part`}
            backgroundColor="smBackground"
            placeholder={t("Choose the part") ?? undefined}
            value={value.foaPart ?? ""}
            onChange={(e) => onChange({ regulation: value.regulation, foaPart: (e.target.value || null) as FoaPart | null })}
          >
            {FOA_PARTS.map((part) => (
              <option key={part} value={part}>
                {t(part)}
              </option>
            ))}
          </Select>
          <FormErrorMessage>
            {errors?.foaPart ? formatFieldError(errors.foaPart) : t("Choose the part of FOA it was let under")}
          </FormErrorMessage>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      )}
    </Stack>
  );
};
